import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { Invitation, OnboardingDraft } from '../domain/onboarding.model';
import { validateDraft } from '../domain/onboarding.validation';
import { toInvitationValidationMessage, toOnboardingError } from '../domain/onboarding-error';
import type { OnboardingGateway } from '../infrastructure/onboarding.gateway';
import { invitationValidationRequested, submitRequested } from './onboarding.actions';
import { selectDraft } from './onboarding.selectors';
import { onboardingActions } from './onboarding.slice';

/**
 * WORKER: resolve the invitee behind the one-time token carried by the action
 * (read from the URL by the page on mount; never stored). All I/O goes
 * through the injected gateway (read from the saga `context`; never the api
 * client), so tests drive the success / api-error / network-error branches
 * with an in-memory gateway.
 */
function* validateInvitationWorker(action: ReturnType<typeof invitationValidationRequested>) {
  yield put(onboardingActions.invitationValidationStarted());
  const gateway: OnboardingGateway = yield getContext('onboardingGateway');
  try {
    const invitation: Invitation = yield call(gateway.validateInvitation, action.payload.token);
    yield put(onboardingActions.invitationValidationSucceeded(invitation));
  } catch (e) {
    yield put(onboardingActions.invitationValidationFailed(toInvitationValidationMessage(e)));
  }
}

/**
 * WORKER: accept the invitation with the drafted password pair and the token
 * carried by the action. Validates up front (a `validation` error
 * short-circuits before any I/O), then calls the gateway. `takeLatest` means a
 * fresh submit supersedes any in-flight request.
 */
function* submitWorker(action: ReturnType<typeof submitRequested>) {
  const draft = (yield select(selectDraft)) as OnboardingDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(onboardingActions.submitFailed({ kind: 'validation', fields: errors }));
    return;
  }

  yield put(onboardingActions.submitStarted());
  const gateway: OnboardingGateway = yield getContext('onboardingGateway');
  try {
    yield call(gateway.acceptInvitation, {
      token: action.payload.token,
      password: draft.password,
      confirmPassword: draft.confirmPassword,
    });
    yield put(onboardingActions.submitSucceeded());
  } catch (e) {
    yield put(onboardingActions.submitFailed(toOnboardingError(e)));
  }
}

/** WATCHERS. */
export function* onboardingSaga() {
  yield all([
    takeLatest(invitationValidationRequested.type, validateInvitationWorker),
    takeLatest(submitRequested.type, submitWorker),
  ]);
}
